/**
 * @file card.tsx
 */

"use client";

import * as React from "react";

import { cn } from "visuais/fmt";

/**
 * @returns {JSX.Element} Cartão principal.
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Título</CardTitle>
 *   </CardHeader>
 *   <CardContent>Conteúdo</CardContent>
 * </Card>
 * ```
 */
export function Card({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card"
      className={cn(
        "bg-gradient-to-t from-input/5 to-card dark:bg-popover/70 text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm backdrop-blur-xs",
        className
      )}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Cabeçalho do cartão.
 * @example
 * ```tsx
 * <CardHeader>
 *   <CardTitle>Título</CardTitle>
 *   <CardDescription>Descrição</CardDescription>
 * </CardHeader>
 * ```
 */
export function CardHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Título do cartão.
 * @example
 * ```tsx
 * <CardTitle>Título</CardTitle>
 * ```
 */
export function CardTitle({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Descrição do cartão.
 * @example
 * ```tsx
 * <CardDescription>Descrição</CardDescription>
 * ```
 */
export function CardDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Ação do cartão.
 * @example
 * ```tsx
 * <CardAction>
 *   <button>Clique</button>
 * </CardAction>
 * ```
 */
export function CardAction({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Conteúdo do cartão.
 * @example
 * ```tsx
 * <CardContent>Conteúdo</CardContent>
 * ```
 */
export function CardContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Rodapé do cartão.
 * @example
 * ```tsx
 * <CardFooter>
 *   <button>Clique</button>
 * </CardFooter>
 * ```
 */
export function CardFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  );
}
